import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { HomeIcon, TruckIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../../../utils/formatters';
import { AddressDropdown } from '../../../../components/address';
import { useGetAddressesQuery } from '../../../../redux/apis';
import { selectDefaultAddress } from '../../../../redux/slices/addressSlice';
import { calculateShippingRate, setSelectedRate, clearShippingError, resetRatesAttempted, setUseEstimator } from '../../../../redux/slices/deliverySlice';
import { selectIsAuthenticated } from '../../../../redux/selectors/authSelectors';
import { selectSellerAddress } from '../../../../redux/slices/productSlice';
import { showError } from '../../../../utils/toast';

// Import the separated components
import ShippingOption from './delivery/ShippingOption';
import PickupOption from './delivery/PickupOption';

/**
 * Shared component for delivery method selection
 * Used in BuyActions, RentActions, and SwapActions
 */
const DeliveryMethodSelection = ({
  openToShipping,
  openToPickUp,
  deliveryOption,
  setDeliveryOption,
  pickUpAddress,
  shippingCost = 5.99,
  currency = 'USD',
  shippingRate = null,
  isShippingRateSelected = false,
  onAddressSelect = null,
  productId = null,
  userLocation = null,
  product,
}) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // Get seller address from Redux store instead of props
  const sellerAddress = useSelector(selectSellerAddress);

  // Only fetch addresses if the user is logged in to avoid 403 errors
  const { data: addresses = [], isLoading: addressesLoading } = useGetAddressesQuery(undefined, {
    skip: !isAuthenticated
  });

  const defaultAddress = useSelector(selectDefaultAddress);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [isCalculatingRates, setIsCalculatingRates] = useState(false);
  const [showAddressSelection, setShowAddressSelection] = useState(false);
  const shippingState = useSelector(state => state.shipping);
  const [rateCalculationAttempted, setRateCalculationAttempted] = useState(false);
  const initializedRef = useRef(false);

  // Log the seller address for debugging
  useEffect(() => {
  }, [sellerAddress]);

  // Clear shipping error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearShippingError());
    };
  }, [dispatch]);

  // Reset rates attempted when product or seller address changes
  useEffect(() => {
    dispatch(resetRatesAttempted());
    setRateCalculationAttempted(false);
  }, [productId, sellerAddress, dispatch]);

  // When shipping is selected but no address, automatically show address selection UI
  useEffect(() => {
    if (deliveryOption === 'shipping' && !selectedAddress && isAuthenticated) {
      // Set a flag to show the address selection UI more prominently
      setShowAddressSelection(true);
    }
  }, [deliveryOption, selectedAddress, isAuthenticated]);

  // When shipping is selected, auto-enable estimator
  useEffect(() => {
    if (deliveryOption === 'shipping') {
      dispatch(setUseEstimator(true));

      // Create estimated rate
      const estimatedRate = {
        provider: 'Estimated',
        serviceName: 'Standard',
        amount: '5.99',
        currency: 'USD',
        days: '1-3',
        isEstimate: true
      };

      dispatch(setSelectedRate(estimatedRate));
    }
  }, [deliveryOption, dispatch]);

  // Memoize the calculateRates function to avoid recreating it on each render
  const calculateRates = useCallback(async (address) => {
    // Only authenticated users can calculate rates with real addresses
    if (!isAuthenticated || !address || !address.id) {
      return;
    }

    // Ensure product and its parcelInfo are available
    if (!product || !product.parcelInfo) {
      console.error('[DeliveryMethodSelection] Product data or parcelInfo is missing. Cannot calculate rates.');
      showError('Product dimension details are missing. Cannot estimate shipping.');
      setIsCalculatingRates(false);
      return;
    }

    // If we're already calculating or have an error, don't try again
    if (isCalculatingRates || shippingState.loading) {
      return;
    }

    // Don't calculate again if we've already attempted with this address
    if (rateCalculationAttempted && shippingState.address?.id === address.id) {
      return;
    }


    setIsCalculatingRates(true);
    setRateCalculationAttempted(true);

    // Use actual product's parcel information
    const productParcel = {
      length: parseFloat(product.parcelInfo.length) || 10, // Fallback for safety
      width: parseFloat(product.parcelInfo.width) || 8,
      height: parseFloat(product.parcelInfo.height) || 2,
      distance_unit: product.parcelInfo.units?.distance || 'in',
      weight: parseFloat(product.parcelInfo.weight) || 1,
      mass_unit: product.parcelInfo.units?.mass || 'lb'
    };


    try {

      // Simplified: No need to pass fromAddress since it's pulled from Redux store
      const result = await dispatch(calculateShippingRate({
        toAddress: address,
        parcels: [productParcel]
      })).unwrap();


      if (result?.length > 0) {
      }
    } catch (error) {
      console.error('Failed to calculate shipping rates:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
    } finally {
      setIsCalculatingRates(false);
    }
  }, [dispatch, isCalculatingRates, productId, rateCalculationAttempted, shippingState.address?.id, shippingState.loading, isAuthenticated, sellerAddress, product]);

  // Simplified check for if an address is present - no need for strict validation
  const hasAddress = !!selectedAddress;

  // No longer use default address on initialization
  useEffect(() => {
    // Mark as initialized
    initializedRef.current = true;
  }, []);

  // Handle address selection
  const handleAddressSelect = (e) => {
    // Skip for non-authenticated users
    if (!isAuthenticated) return;

    const addressId = e.target.value;
    if (!addressId) {
      // Clear selected address if empty value is selected
      setSelectedAddress(null);
      setSelectedAddressId('');

      if (onAddressSelect) {
        onAddressSelect(null);
      }
      return;
    }

    // Handle "Add New Address" option
    if (addressId === 'new') {
      // This would typically open an address form modal
      return;
    }

    // Clear any previous shipping errors when address changes
    dispatch(clearShippingError());

    const address = addresses.find(addr => addr.id === addressId);
    if (address) {
      setSelectedAddress(address);
      setSelectedAddressId(addressId);

      if (onAddressSelect) {
        onAddressSelect(address);
      }

      // Reset rate calculation status since address changed
      setRateCalculationAttempted(false);
      dispatch(resetRatesAttempted());

      // Calculate shipping rates if shipping is selected as delivery option
      if (openToShipping && deliveryOption === 'shipping' && productId && sellerAddress) {
        // Calculate rates immediately instead of using setTimeout
        calculateRates(address);
      }
    }
  };

  // Calculate distance between pickup location and user address
  const calculateDistance = () => {
    if (!pickUpAddress || !selectedAddress ||
      !pickUpAddress.latitude || !pickUpAddress.longitude ||
      !selectedAddress.latitude || !selectedAddress.longitude) {
      return null;
    }

    // Haversine formula to calculate distance between two points
    const toRad = (value) => value * Math.PI / 180;
    const R = 6371; // Earth radius in km

    const dLat = toRad(selectedAddress.latitude - pickUpAddress.latitude);
    const dLon = toRad(selectedAddress.longitude - pickUpAddress.longitude);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(pickUpAddress.latitude)) * Math.cos(toRad(selectedAddress.latitude)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    // Return in miles if country is US, otherwise km
    if (selectedAddress.country === 'US') {
      return `${Math.round(distance * 0.621371)} miles away`;
    }
    return `${Math.round(distance)} km away`;
  };

  // Handle delivery option selection
  const handleDeliveryOptionSelect = (option) => {
    // If the option is already selected, unselect it
    if (option === deliveryOption) {
      setDeliveryOption(null); // Unselect
    } else {
      setDeliveryOption(option);
    }
  };

  // Only show delivery options if at least one is available
  const showDeliveryOptions = openToShipping || openToPickUp;

  if (!showDeliveryOptions) {
    return (
      <div className="bg-yellow-50 p-4 rounded-lg">
        <p className="text-sm text-yellow-700">
          No delivery options are available for this item.
        </p>
      </div>
    );
  }

  // Get Shippo rates from redux state
  const availableRates = shippingState.shippingRates || [];
  const selectedRate = shippingState.selectedRate;
  const shippingRateLoading = shippingState.loading;

  // Debug log shipping state and selected rate

  return (
    <div>
      {/* Remove Address Selection Dropdown and always use estimator */}

      {/* Delivery Method Selection */}
      <div className="space-y-3">
        <div className="space-y-2">
          {/* Shipping Option */}
          {openToShipping && (
            <ShippingOption
              deliveryOption={deliveryOption}
              setDeliveryOption={handleDeliveryOptionSelect}
              selectedAddress={null} // Always use estimator by not providing address
              shippingRateLoading={false}
              isCalculatingRates={false}
              availableRates={[]}
              selectedRate={null}
              handleShippingRateSelect={calculateRates}
              calculateRates={calculateRates}
              sellerAddress={sellerAddress}
              productId={productId}
              shippingState={{ ...shippingState, useEstimator: true }} // Force estimator mode
              currency={currency}
              product={product}
            />
          )}

          {/* Pickup Option */}
          {openToPickUp && (
            <PickupOption
              deliveryOption={deliveryOption}
              setDeliveryOption={handleDeliveryOptionSelect}
              pickUpAddress={pickUpAddress || sellerAddress}
              selectedAddress={null}
              calculateDistance={calculateDistance}
              userLocation={userLocation?.coordinates}
              sellerAddress={sellerAddress}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryMethodSelection;